import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IFilter,
  IFiltersForm,
  IKPIFormField,
  IKPIFormState,
} from './KPIForm.interface';
import { getFilters } from 'src/services/KPI/KPIForm/KPIForm.service';
import { getQuestiosForm } from 'src/services/KPI/Questions/Questions.service';

/**
 * The initial state for the KPI Form slice.
 *
 * @constant
 * @type {IKPIFormState}
 */
const initialState: IKPIFormState = {
  filter: {
    areaName: null,
    monthNumber: null,
    periodId: null,
    quarterId: null,
    channelId: null,
  },
  filters: [],
  kpiForm: {
    indicatorId: 0,
    indicatorDescription: '',
    indicatorName: '',
    labelOne: '',
    labelTwo: '',
    indicatorQuestions: [],
  },
  kpiFormList: [],
  error: null,
  loading: false,
};

/**
 * A slice of the Redux store responsible for managing KPI Form data.
 *
 * This slice contains actions and reducers to control the loading state,
 * manage KPI forms, filters, and handle API responses for retrieving questions and filters.
 *
 * @constant
 * @type {Slice<IKPIFormState>}
 */
const KPIFormSlice = createSlice({
  name: 'KPIForm',
  initialState,
  reducers: {
    /**
     * Sets a field in the KPI form based on the provided payload.
     *
     * @param {IKPIFormState} state - The current state of the KPIForm slice.
     * @param {PayloadAction<IKPIFormField>} action - The action containing the new field data.
     */
    setField: (state, action: PayloadAction<IKPIFormField>) => {
      state.kpiForm = { ...state.kpiForm, ...action.payload };
    },

    /**
     * Sets the filter based on the provided payload.
     *
     * @param {IKPIFormState} state - The current state of the KPIForm slice.
     * @param {PayloadAction<IFilter>} action - The action containing the new filter values.
     */
    setFilter: (state, action: PayloadAction<IFilter>) => {
      state.filter = { ...state.filter, ...action.payload };
    },

    /**
     * Sets the entire KPI form data based on the provided payload.
     *
     * @param {IKPIFormState} state - The current state of the KPIForm slice.
     * @param {PayloadAction<IKPIFormField>} action - The action containing the new KPI form data.
     */
    setKPIForm: (state, action: PayloadAction<IKPIFormField>) => {
      state.kpiForm = action.payload;
    },

    /**
     * Resets the filters to the initial state.
     *
     * @param {IKPIFormState} state - The current state of the KPIForm slice.
     */
    resetFilters: (state) => {
      state.filter = initialState.filter;
    },

    /**
     * Resets the KPI form fields, clearing the kpiFormList.
     *
     * @param {IKPIFormState} state - The current state of the KPIForm slice.
     */
    resetKPIFormFields: (state) => {
      state.kpiFormList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getQuestiosForm.fulfilled,
        (state, action: PayloadAction<IKPIFormField[]>) => {
          state.loading = false;
          state.kpiFormList = action.payload;
        },
      )
      .addCase(
        getQuestiosForm.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        },
      )
      .addCase(
        getFilters.fulfilled,
        (state, action: PayloadAction<IFiltersForm[]>) => {
          state.loading = false;
          state.filters = action.payload;
        },
      )
      .addCase(getFilters.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

/**
 * Action creators generated from the KPIForm slice.
 *
 * @constant
 * @type {{ setField: (field: IKPIFormField) => PayloadAction<IKPIFormField>, setFilter: (filter: IFilter) => PayloadAction<IFilter>, setKPIForm: (kpiForm: IKPIFormField) => PayloadAction<IKPIFormField>, resetFilters: () => PayloadAction<void>, resetKPIFormFields: () => PayloadAction<void> }}
 */
export const {
  setKPIForm,
  setField,
  setFilter,
  resetFilters,
  resetKPIFormFields,
} = KPIFormSlice.actions;

/**
 * The reducer for the KPIForm slice, to be used in the Redux store.
 *
 * @constant
 * @type {Reducer<IKPIFormState>}
 */
export default KPIFormSlice.reducer;
